import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import './CustomTooltip.css'

const CustomTooltip = ({ children, content, targetRef }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [isTouchDevice, setIsTouchDevice] = useState(false)
  const timeoutRef = useRef(null)
  const tooltipRef = useRef()

  const updateTooltipPosition = useCallback(() => {
    const tooltipNode = tooltipRef.current
    if (tooltipNode && targetRef.current) {
      const targetRect = targetRef.current.getBoundingClientRect()
      const top = targetRect.bottom + window.scrollY
      const left =
        targetRect.left +
        window.scrollX +
        targetRect.width / 2 -
        tooltipNode.offsetWidth / 2 // Center horizontally

      tooltipNode.style.top = `${top}px`
      tooltipNode.style.left = `${left}px`
    }
  }, [targetRef])

  const showTooltip = () => {
    clearTimeout(timeoutRef.current)
    setIsVisible(true)
  }

  const hideTooltip = () => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false)
    }, 1500) // Adjust delay as needed
  }

  const toggleTooltipOnTouch = () => {
    if (isTouchDevice) {
      setIsVisible(prevIsVisible => !prevIsVisible)
    }
  }

  useEffect(() => {
    if (isVisible) {
      updateTooltipPosition()
    }
  }, [isVisible, updateTooltipPosition]) // Recalculate position when the tooltip becomes visible

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current) // Cleanup timeout on component unmount
  }, [])

  // Detect if the device is a touch device
  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0)
  }, [])

  return isVisible
    ? createPortal(
        <div
          className="tooltip-content"
          ref={tooltipRef}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
          onTouchEnd={toggleTooltipOnTouch}
          style={{
            position: 'fixed',
            visibility: isVisible ? 'visible' : 'hidden',
          }}
        >
          {content}
        </div>,
        document.body,
      )
    : null
}

export default CustomTooltip
